import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    AlertCircle,
    ArrowLeft,
    CalendarDays,
    Clock3,
    FileText,
    Image as ImageIcon,
    MapPin,
    Navigation,
    ReceiptText,
    RefreshCw,
    Route,
    ShieldCheck,
    Star,
    UserRound,
    Wrench,
} from 'lucide-react';
import { ServiceRequest } from '../models/ServiceRequest';
import { RequestService } from '../services/requestService';
import { formatDateString, formatTimeString, parseDateString } from '../lib/utils';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ReceiptModal } from './ReceiptModal';

const formatDateTime = (dateTime?: string | null) => {
    if (!dateTime) return '-';
    const date = parseDateString(dateTime);
    if (!date) return 'Invalid date';
    return `${formatDateString(date)} at ${formatTimeString(date)}`;
};

const getStatusClasses = (status: string) => {
    const styles: Record<string, string> = {
        Accepted: 'border-orange-200 bg-orange-50 text-orange-700',
        Completed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        Cancelled: 'border-slate-200 bg-slate-100 text-slate-700',
        Pending: 'border-amber-200 bg-amber-50 text-amber-700',
        'Time Out': 'border-red-200 bg-red-50 text-red-700',
    };

    return styles[status] || 'border-slate-200 bg-slate-100 text-slate-700';
};

interface DetailCardProps {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}

const DetailCard: React.FC<DetailCardProps> = ({ title, icon, children, className = '' }) => (
    <Card className={`overflow-hidden rounded-xl border-slate-200 shadow-[0_12px_30px_-26px_rgba(15,23,42,0.55)] ${className}`}>
        <CardHeader className="flex-row items-center gap-3 space-y-0 border-b border-slate-100 px-5 py-4">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                {icon}
            </span>
            <CardTitle className="text-base font-semibold tracking-tight text-slate-900">{title}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">{children}</CardContent>
    </Card>
);

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="grid gap-1 border-b border-slate-100 px-5 py-3.5 last:border-b-0 sm:grid-cols-[minmax(7rem,0.8fr)_minmax(0,1.2fr)] sm:gap-4">
        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</dt>
        <dd className="min-w-0 break-words text-sm font-medium leading-6 text-slate-800">{children}</dd>
    </div>
);

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
    <div className="flex items-center gap-1" aria-label={`${rating} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((star) => (
            <Star
                key={star}
                className={`h-4 w-4 ${star <= rating ? 'fill-amber-400 text-amber-400' : 'fill-slate-100 text-slate-300'}`}
                aria-hidden="true"
            />
        ))}
        <span className="ml-1 text-xs font-semibold text-slate-500">{rating}/5</span>
    </div>
);

export const RequestDetails: React.FC = () => {
    const { requestId } = useParams<{ requestId: string }>();
    const navigate = useNavigate();
    const [request, setRequest] = useState<ServiceRequest | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

    useEffect(() => {
        const fetchRequest = async () => {
            if (!requestId) {
                setError('Request not found');
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setError(null);
                const data = await RequestService.getRequestDetails(requestId);
                setRequest(data);
            } catch (err) {
                setError('Failed to load request details');
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRequest();
    }, [requestId]);

    if (isLoading) {
        return (
            <div className="flex min-h-[28rem] flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white text-center shadow-sm" role="status">
                <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                    <RefreshCw className="h-6 w-6 animate-spin" aria-hidden="true" />
                </span>
                <h2 className="text-base font-semibold text-slate-900">Loading request details</h2>
                <p className="mt-1 text-sm text-slate-500">Retrieving the latest request information.</p>
            </div>
        );
    }

    if (error || !request) {
        return (
            <div className="flex min-h-[28rem] flex-col items-center justify-center rounded-2xl border border-red-200 bg-white px-6 text-center shadow-sm" role="alert">
                <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 text-red-600">
                    <AlertCircle className="h-6 w-6" aria-hidden="true" />
                </span>
                <h2 className="text-lg font-semibold text-slate-900">Unable to open request</h2>
                <p className="mt-2 max-w-md text-sm text-slate-500">{error || 'Request not found'}</p>
                <Button
                    type="button"
                    onClick={() => navigate('/requests')}
                    className="mt-6 bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
                >
                    <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                    Back to Requests
                </Button>
            </div>
        );
    }

    const details = request.requestDetails;
    const requestDescription = details?.description || request.description;
    const serviceType = details?.serviceType || request.serviceType || '-';
    const category = details?.category || request.category || '-';
    const locationAddress = [
        request.location.requestAddress,
        request.location.city,
        request.location.state,
        request.location.zipCode,
    ].filter(Boolean).join(', ');

    return (
        <div className="mx-auto max-w-7xl space-y-6 pb-8">
            <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 px-5 py-6 shadow-[0_20px_50px_-36px_rgba(15,23,42,0.9)] sm:px-6 sm:py-7">
                <div className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" aria-hidden="true" />
                <div className="pointer-events-none absolute -bottom-24 left-1/3 h-52 w-52 rounded-full bg-cyan-400/10 blur-3xl" aria-hidden="true" />

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => navigate('/requests')}
                        className="mb-5 inline-flex items-center gap-2 rounded-lg text-sm font-semibold text-slate-300 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-4 focus-visible:ring-offset-slate-950"
                    >
                        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                        Back to Requests
                    </button>

                    <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <div className="mb-3 flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center gap-2 rounded-full border border-blue-300/20 bg-blue-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-200">
                                    <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                                    Service request
                                </span>
                                <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getStatusClasses(request.status)}`}>
                                    {request.status}
                                </span>
                            </div>
                            <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                                Request {request.requestId}
                            </h1>
                            <p className="mt-2 text-sm leading-6 text-slate-300">
                                Complete operational record for this service request.
                            </p>
                        </div>

                        <Button
                            type="button"
                            onClick={() => setIsReceiptModalOpen(true)}
                            disabled={!request.receiptUrl}
                            className="h-10 rounded-xl bg-blue-600 px-4 text-white shadow-[0_12px_28px_-16px_rgba(37,99,235,0.95)] hover:bg-blue-500 focus:ring-cyan-300 focus:ring-offset-slate-950 disabled:bg-white/10 disabled:text-slate-400 disabled:shadow-none"
                        >
                            <ReceiptText className="mr-2 h-4 w-4" aria-hidden="true" />
                            {request.receiptUrl ? 'View Receipt' : 'Receipt Unavailable'}
                        </Button>
                    </div>
                </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Request summary">
                {[
                    { label: 'Requested', value: formatDateTime(request.requestDateAndTime), icon: <CalendarDays className="h-5 w-5" /> },
                    { label: 'Distance', value: `${request.distance} miles`, icon: <Route className="h-5 w-5" /> },
                    { label: 'Service type', value: serviceType, icon: <Wrench className="h-5 w-5" /> },
                    { label: 'Urgency', value: details?.urgency || '-', icon: <Clock3 className="h-5 w-5" /> },
                ].map((item) => (
                    <div key={item.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                            <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-blue-50 text-blue-700" aria-hidden="true">
                                {item.icon}
                            </span>
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{item.label}</p>
                                <p className="mt-1 break-words text-sm font-semibold capitalize leading-5 text-slate-900">{item.value}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            <div className="grid gap-6 xl:grid-cols-3">
                <div className="space-y-6 xl:col-span-2">
                    <DetailCard title="Request information" icon={<FileText className="h-[18px] w-[18px]" aria-hidden="true" />}>
                        <dl className="grid md:grid-cols-2 md:[&>div:nth-child(odd)]:border-r md:[&>div:nth-last-child(-n+2)]:border-b-0">
                            <DetailRow label="Category">{category}</DetailRow>
                            <DetailRow label="Preferred time">{formatDateTime(details?.preferredTime)}</DetailRow>
                            <DetailRow label="Request ID">{request.requestId}</DetailRow>
                            <DetailRow label="Completion OTP">{request.completionOTP || '-'}</DetailRow>
                        </dl>
                    </DetailCard>

                    {requestDescription && (
                        <DetailCard title="Description" icon={<FileText className="h-[18px] w-[18px]" aria-hidden="true" />}>
                            <p className="px-5 py-4 text-sm leading-7 text-slate-700">{requestDescription}</p>
                        </DetailCard>
                    )}

                    <div className="grid gap-6 md:grid-cols-2">
                        <DetailCard title="Service user" icon={<UserRound className="h-[18px] w-[18px]" aria-hidden="true" />}>
                            <dl>
                                <DetailRow label="Name">{request.requestor.name}</DetailRow>
                                <DetailRow label="Contact">{request.requestor.contactNumber}</DetailRow>
                            </dl>
                        </DetailCard>

                        <DetailCard title="Service provider" icon={<Wrench className="h-[18px] w-[18px]" aria-hidden="true" />}>
                            <dl>
                                <DetailRow label="Name">{request.requestee.name}</DetailRow>
                                <DetailRow label="Contact">{request.requestee.contactNumber}</DetailRow>
                            </dl>
                        </DetailCard>
                    </div>

                    {details?.photos && details.photos.length > 0 && (
                        <DetailCard title="Request photos" icon={<ImageIcon className="h-[18px] w-[18px]" aria-hidden="true" />}>
                            <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
                                {details.photos.map((photo, index) => (
                                    <a
                                        key={`${photo}-${index}`}
                                        href={photo}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="group overflow-hidden rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                                    >
                                        <img
                                            src={photo}
                                            alt={`Request ${request.requestId}, attachment ${index + 1}`}
                                            className="h-36 w-full object-cover"
                                        />
                                    </a>
                                ))}
                            </div>
                        </DetailCard>
                    )}
                </div>

                <aside className="space-y-6">
                    {request.quoteDetails && (
                        <DetailCard title="Quote information" icon={<ReceiptText className="h-[18px] w-[18px]" aria-hidden="true" />}>
                            <dl>
                                <DetailRow label="Amount">
                                    <span className="text-base text-slate-950">
                                        {request.quoteDetails.currency || 'USD'} {request.quoteDetails.amount.toFixed(2)}
                                    </span>
                                </DetailRow>
                                <DetailRow label="Quote ID">{request.quoteDetails.quoteId || '-'}</DetailRow>
                                <DetailRow label="Status">
                                    <span className="capitalize">{request.quoteDetails.status}</span>
                                </DetailRow>
                                <DetailRow label="Warranty">{request.quoteDetails.warranty || '-'}</DetailRow>
                                {request.quoteDetails.remarks && (
                                    <DetailRow label="Remarks">{request.quoteDetails.remarks}</DetailRow>
                                )}
                            </dl>
                        </DetailCard>
                    )}

                    <DetailCard title="Location details" icon={<MapPin className="h-[18px] w-[18px]" aria-hidden="true" />}>
                        <dl>
                            <DetailRow label="Address">{locationAddress || '-'}</DetailRow>
                            <DetailRow label="Coordinates">
                                <span className="inline-flex items-start gap-2">
                                    <Navigation className="mt-1 h-3.5 w-3.5 flex-none text-blue-600" aria-hidden="true" />
                                    {request.location.coordinates.latitude}, {request.location.coordinates.longitude}
                                </span>
                            </DetailRow>
                        </dl>
                    </DetailCard>

                    <DetailCard title="Ratings & reviews" icon={<Star className="h-[18px] w-[18px]" aria-hidden="true" />}>
                        <div className="border-b border-slate-100 px-5 py-4 last:border-b-0">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Service provider</p>
                            <StarRating rating={request.ratings.serviceProvider.rating} />
                            <p className="mt-2 text-sm leading-6 text-slate-600">{request.ratings.serviceProvider.review || 'No review provided'}</p>
                        </div>
                        <div className="px-5 py-4">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Service user</p>
                            <StarRating rating={request.ratings.serviceUser.rating} />
                            <p className="mt-2 text-sm leading-6 text-slate-600">{request.ratings.serviceUser.review || 'No review provided'}</p>
                        </div>
                    </DetailCard>
                </aside>
            </div>

            {request.receiptUrl && (
                <ReceiptModal
                    isOpen={isReceiptModalOpen}
                    onClose={() => setIsReceiptModalOpen(false)}
                    receiptUrl={request.receiptUrl}
                />
            )}
        </div>
    );
};
